import { useEffect, useState } from 'react'
import { appendSearchParams } from '@/utils'
import { useLocalStorage } from '@/hooks/index'
import { useRouter } from 'next/router'
import { EmtDrawer, EmtDrawerProps, useMobile } from 'emt-components-v2'
import Filters from '@/components/search/Filters'
import {
  filtersValuesKeys,
  generateDefaultFiltersValues,
  SearchFiltersKeys,
  SearchFiltersValues,
  SearchType,
} from '@/modules/Search/apiTypes/base'

type Props = {
  onSearchClick?: (filters?: SearchFiltersValues) => void
  onShortFilterChange?: () => void
  activeLocalStorage?: boolean
  activeHandleRouter?: boolean
  localStorageFiltersKey?: string
  localStorageShortFiltersKey?: string
  filtersComponentProps?: EmtDrawerProps
  disabledFilters?: Array<keyof SearchFiltersValues>
}

const countFilters = (filters?: SearchFiltersValues | null) => {
  if (!filters) {
    return 0
  }

  return Object.keys(filters).filter((filterKey) => {
    if (Array.isArray(filters[filterKey])) {
      return filters[filterKey]?.length
    }
    if (typeof filters[filterKey] === 'string') {
      return !!filters[filterKey] && filters[filterKey] !== ''
    }
    if (filterKey === 'distance' && filters[filterKey] === 5) {
      return false
    }
    return !!filters[filterKey]
  }).length
}

const useFilters = ({
  onShortFilterChange,
  onSearchClick,
  activeLocalStorage = false,
  activeHandleRouter = false,
  localStorageFiltersKey = 'filters',
  localStorageShortFiltersKey = 'shortFilter',
  filtersComponentProps = { variant: 'temporary' },
  disabledFilters,
}: Props) => {
  const router = useRouter()
  const [isMobile] = useMobile()

  // localStorage
  const [localStorageFilters, setLocalStorageFilters] = useLocalStorage(
    localStorageFiltersKey,
    generateDefaultFiltersValues(),
  )
  const [localStorageShortFilter, setLocalStorageShortFilter] = useLocalStorage(
    localStorageShortFiltersKey,
    SearchType.recommendation,
  )

  // Filters state
  const [filters, setFilters] = useState<SearchFiltersValues>()
  const [shortFilter, setShortFilter] = useState<SearchType>(localStorageShortFilter)
  const [totalFilters, setTotalFilters] = useState<number>(0)

  const [activeFilterSidebar, setActiveFilterSidebar] = useState<boolean>(false)

  const handleActiveFilter = () => {
    setActiveFilterSidebar((prev) => !prev)
  }

  const handleFiltersChange = (filtersValues: SearchFiltersValues) => {
    setFilters(filtersValues)
    activeLocalStorage && setLocalStorageFilters(filtersValues)

    if (!isMobile && activeHandleRouter) {
      router.replace(appendSearchParams(router.asPath, { ...filtersValues, searchType: shortFilter }), undefined, {
        shallow: true,
      })
    }
  }

  const handleSearchClick = (filtersValues: SearchFiltersValues) => {
    if (filtersValues) {
      setFilters(filtersValues)
      setTotalFilters(countFilters(filtersValues))
      activeLocalStorage && setLocalStorageFilters(filtersValues)
    }

    if (activeHandleRouter) {
      router.replace(
        appendSearchParams(router.asPath, { ...(filtersValues ? filtersValues : filters), searchType: shortFilter }),
        undefined,
        {
          shallow: true,
        },
      )
    }

    handleActiveFilter()

    onSearchClick && onSearchClick(filtersValues ? filtersValues : filters)
  }

  const handleShortFilterClick = (filterName: SearchType) => {
    setShortFilter((oldFilterName) => (oldFilterName === filterName ? SearchType.recommendation : filterName))
    activeLocalStorage &&
      setLocalStorageShortFilter(shortFilter === filterName ? SearchType.recommendation : filterName)

    if (activeHandleRouter) {
      router.replace(
        appendSearchParams(router.asPath, {
          ...filters,
          searchType: filterName,
        }),
        undefined,
        {
          shallow: true,
        },
      )
    }

    onShortFilterChange && onShortFilterChange()
  }

  // 🔍 Load Default filters state
  useEffect(() => {
    if (activeLocalStorage) {
      const baseFiltersValues = localStorageFilters

      if (activeHandleRouter) {
        Object.keys(router.query).forEach((key) => {
          if (key === 'searchType') setShortFilter(router.query[key] as SearchType)
          if (filtersValuesKeys.includes(key as SearchFiltersKeys)) {
            baseFiltersValues[key] = ['size', 'breed', 'species'].includes(key)
              ? (router.query[key] as string).split(',')
              : router.query[key] === 'true'
              ? true
              : router.query[key] === 'false'
              ? false
              : router.query[key]
          }
        })
      }

      setFilters(baseFiltersValues)
    }
  }, [])

  useEffect(() => {
    setTotalFilters(countFilters(filters))
  }, [filters])

  const FiltersComponent = () => {
    if (filters) {
      return (
        <EmtDrawer
          onTop={true}
          anchor={isMobile ? 'bottom' : 'left'}
          variant={filtersComponentProps.variant}
          sx={{ borderRadius: 0, paddingRight: 0, ...(filtersComponentProps.sx ? filtersComponentProps.sx : {}) }}
          open={activeFilterSidebar}
          onClose={handleActiveFilter}
        >
          <Filters
            manualRequest
            defaultFiltersValues={filters}
            shortFilter={shortFilter}
            onFilterChange={handleFiltersChange}
            onEraseFilter={handleSearchClick}
            onSearchButtonClick={handleSearchClick}
            onClose={handleActiveFilter}
            disabledFilters={disabledFilters}
          />
        </EmtDrawer>
      )
    }
    return null
  }

  return {
    filters,
    shortFilter,
    totalFilters,
    activeFilterSidebar,
    handleFiltersChange,
    handleSearchClick,
    handleShortFilterClick,
    handleActiveFilter,
    FiltersComponent,
  }
}

export default useFilters
